import { type Editor, type Path, Text } from 'slate';
import type {
  TableElement,
  TableRowElement,
  TableCellElement,
} from '../../types/aha-editor';
import memoizeOne from 'memoize-one';
import TableRange from './TableRange';
import type TableSelection from './TableSelection';
import TableUtils from './TableUtils';

// Cell coordinates are relative to the internal cells structure of TableStructure.
export interface CellCoordinates {
  rowIndex: number;
  columnIndex: number;
}

// Information about one cell in the table.
export interface CellInfo {
  // The relative path is a 2 element array - [row, cell].
  relativePath: Path;
  tableCell: TableCellElement;
  tableRow: TableRowElement;
  isActual: boolean; // Whether this cell is spanned. The top-left cell is actual.
}

// A relative direction to a cell.
export type CellDirection = 'up' | 'down' | 'left' | 'right';

// Convert the HTML table structure into a two dimensional array of cells
// that can be used to introspect and modify the table.
//
// The HTML table may be sparse because of rowspan and colspan, but in the
// array here every cell is populated.
//
// This code assumes the editor is normalized and thus the table is valid.
//
// After the table is mutated this structure should be discarded and recreated.
class TableStructure {
  static closestEmptyColumn(row: number, column: number, cells: CellInfo[][]) {
    const rowData = cells[row];
    if (!rowData) return column;

    for (
      let columnIndex = column;
      columnIndex < rowData.length;
      columnIndex++
    ) {
      if (!rowData[columnIndex]) return columnIndex;
    }

    return rowData.length;
  }

  editor: Editor;
  tableElement: TableElement;
  cells: CellInfo[][];
  // pathToCoordsCache stores relative cell paths.
  // The relative path is a 2 element array - [row, cell].
  pathToCoordsCache: Map<string, CellCoordinates>;

  // Call fromTableElement rather than new to create a new instance.
  constructor(editor: Editor, tableElement: TableElement) {
    this.editor = editor;
    this.tableElement = tableElement;

    this.buildCells();
  }

  buildCells() {
    const cells: CellInfo[][] = [];
    const pathToCoordsCache = new Map<string, CellCoordinates>();

    this.tableElement.children.forEach(
      (rowElement: TableRowElement, row: number) => {
        let currentColumn = 0;

        rowElement.children.forEach(
          (cellElement: TableCellElement | Text, col: number) => {
            // We keep some empty rows to help with calculating rowSpans.
            if (Text.isText(cellElement)) {
              return;
            }

            const relativePath = [row, col];
            const rowSpan = Number(cellElement.table_rowspan ?? 1);
            const colSpan = Number(cellElement.table_colspan ?? 1);
            const emptyColumn = TableStructure.closestEmptyColumn(
              row,
              currentColumn,
              cells
            );
            let isActual = true;

            for (let rowIndex = row; rowIndex < row + rowSpan; rowIndex++) {
              for (let columnIndex = 0; columnIndex < colSpan; columnIndex++) {
                if (!cells[rowIndex]) cells[rowIndex] = [];

                cells[rowIndex][emptyColumn + columnIndex] = {
                  relativePath,
                  tableCell: cellElement,
                  tableRow: rowElement,
                  isActual,
                };

                if (isActual) {
                  pathToCoordsCache.set(relativePath.toString(), {
                    rowIndex,
                    columnIndex: emptyColumn + columnIndex,
                  });
                }

                isActual = false;
              }
            }

            currentColumn = emptyColumn + colSpan - 1;
          }
        );
      }
    );

    this.cells = cells;
    this.pathToCoordsCache = pathToCoordsCache;
  }

  get width(): number {
    return this.cells[0].length;
  }

  get height(): number {
    return this.cells.length;
  }

  get rows(): CellInfo[][] {
    return [...this.cells];
  }

  cell(x: number, y: number): CellInfo | undefined {
    if (y >= this.cells.length) return;
    if (x >= this.cells[y].length) return;
    return this.cells[y][x];
  }

  actualColumnIndex(columnIndex: number, rowIndex: number): number {
    let i = 0;
    for (let x = 0; x < columnIndex; x++) {
      if (this.cell(x, rowIndex)?.isActual) i++;
    }
    return i;
  }

  actualCellsInColumn(columnIndex: number) {
    const result: CellCoordinates[] = [];
    const found = new Set<string>();

    for (let y = 0; y < this.height; y++) {
      const cell = this.cell(columnIndex, y);
      if (!cell) continue;

      if (!found.has(cell.relativePath.toString())) {
        found.add(cell.relativePath.toString());
        result.push({ rowIndex: y, columnIndex });
      }
    }

    return result;
  }

  actualCellsInRow(rowIndex: number) {
    const result: CellCoordinates[] = [];
    const found = new Set<string>();

    for (let x = 0; x < this.width; x++) {
      const cell = this.cell(x, rowIndex);
      if (!cell) continue;

      if (!found.has(cell.relativePath.toString())) {
        found.add(cell.relativePath.toString());
        result.push({ rowIndex, columnIndex: x });
      }
    }

    return result;
  }

  // Given the relative path to a cell, find the coordinates of the cell in
  // the table taking into account spanned cells.
  // The relative path is a 2 element array - [row, cell].
  pathToCoordinates(path: Path): CellCoordinates | undefined {
    return this.pathToCoordsCache.get(path.toString());
  }

  // Find the next actual cell in the given direction from `startCell`. Returns
  // `null` if the movement would go out of the table.
  nextActualCell(
    startCell: CellCoordinates,
    direction: CellDirection
  ): CellCoordinates | null {
    let delta: CellCoordinates;
    switch (direction) {
      case 'left':
        delta = { columnIndex: -1, rowIndex: 0 };
        break;
      case 'right':
        delta = { columnIndex: 1, rowIndex: 0 };
        break;
      case 'up':
        delta = { columnIndex: 0, rowIndex: -1 };
        break;
      case 'down':
        delta = { columnIndex: 0, rowIndex: 1 };
        break;
    }

    // Move through the table until we find an actual cell.
    let current = startCell;
    do {
      // Move the cell.
      current = {
        columnIndex: current.columnIndex + delta.columnIndex,
        rowIndex: current.rowIndex + delta.rowIndex,
      };

      // Check if we wrapped to another row.
      if (current.columnIndex < 0) {
        current.rowIndex--;
        current.columnIndex = this.width - 1;
      }
      if (current.columnIndex >= this.width) {
        current.rowIndex++;
        current.columnIndex = 0;
      }

      // Check if we moved out of the table.
      if (current.rowIndex < 0) return null;
      if (current.rowIndex >= this.height) return null;
    } while (!this.cell(current.columnIndex, current.rowIndex)?.isActual);

    return current;
  }

  expandRangeForMergedCells(range: TableRange): TableRange {
    const { focusPosition, startPosition, endPosition } = range;
    const focusCell = this.cell(
      focusPosition.columnIndex,
      focusPosition.rowIndex
    );
    if (!focusCell) return range;

    const focusCellPosition = this.pathToCoordinates(focusCell.relativePath);
    if (!focusCellPosition) return range;

    const newStartPosition = {
      rowIndex: startPosition.rowIndex,
      columnIndex: Math.min(
        startPosition.columnIndex,
        focusCellPosition.columnIndex
      ),
    };

    const newEndPosition = {
      rowIndex: endPosition.rowIndex,
      columnIndex: Math.max(
        endPosition.columnIndex,
        focusCellPosition.columnIndex +
          (focusCell.tableCell.table_colspan ?? 1) -
          1
      ),
    };

    return new TableRange(newStartPosition, newEndPosition);
  }

  forEachCellInExpandedRange(
    range: TableRange,
    callback: (cell: CellInfo, rowIndex: number, columnIndex: number) => void
  ) {
    const expandedRange = this.expandRangeForMergedCells(range);

    const startRow = expandedRange.startPosition.rowIndex;
    const endRow = expandedRange.endPosition.rowIndex;
    const startCol = expandedRange.startPosition.columnIndex;
    const endCol = expandedRange.endPosition.columnIndex;

    for (let y = startRow; y <= endRow; y++) {
      for (let x = startCol; x <= endCol; x++) {
        const cell = this.cell(x, y);
        if (cell) {
          callback(cell, y, x);
        }
      }
    }
  }

  // Note that this returns relative paths.
  // The relative path is a 2 element array - [row, cell].
  cellPathsInRange(range: TableRange) {
    const cellPaths = new Set<string>();

    this.forEachCellInExpandedRange(range, (cell, rowIndex, columnIndex) => {
      cellPaths.add(cell.relativePath.toString());
    });

    return cellPaths;
  }

  mergedCellsInSelection(tableSelection: TableSelection) {
    return Array.from(tableSelection.selectedCellPaths).reduce<CellInfo[]>(
      (acc, path) => {
        const coords = this.pathToCoordinates(path);
        if (!coords) return acc;

        const cell = this.cell(coords.columnIndex, coords.rowIndex);
        if (
          cell &&
          ((cell.tableCell.table_rowspan ?? 1) > 1 ||
            (cell.tableCell.table_colspan ?? 1) > 1)
        ) {
          acc.push(cell);
        }
        return acc;
      },
      []
    );
  }

  canMergeCells(tableSelection: TableSelection) {
    let minRow = this.height;
    let minCol = this.width;
    let maxRow = -1;
    let maxCol = -1;

    for (const path of tableSelection.selectedCellPaths) {
      const coords = this.pathToCoordinates(path);
      if (!coords) return false;

      const cell = this.cell(coords.columnIndex, coords.rowIndex);
      if (!cell) return false;

      minRow = Math.min(minRow, coords.rowIndex);
      minCol = Math.min(minCol, coords.columnIndex);
      maxRow = Math.max(
        maxRow,
        coords.rowIndex + ((cell.tableCell.table_rowspan ?? 1) - 1)
      );
      maxCol = Math.max(
        maxCol,
        coords.columnIndex + ((cell.tableCell.table_colspan ?? 1) - 1)
      );
    }

    for (let y = minRow; y <= maxRow; y++) {
      for (let x = minCol; x <= maxCol; x++) {
        const cell = this.cell(x, y);
        if (!cell) return false;

        if (!tableSelection.selectedCells.has(cell.relativePath.toString())) {
          return false;
        }
      }
    }

    return true;
  }

  canSplitCells(tableSelection: TableSelection) {
    return this.mergedCellsInSelection(tableSelection).length > 0;
  }

  missingCellPositions() {
    const maxColumns = Math.max(0, ...this.cells.map((row) => row.length));
    const missingCellPositions: CellCoordinates[] = [];

    this.cells.forEach((row, rowIndex) => {
      if (row.length < maxColumns) {
        for (
          let columnIndex = row.length;
          columnIndex < maxColumns;
          columnIndex++
        ) {
          missingCellPositions.push({ rowIndex, columnIndex });
        }
      }
    });

    return missingCellPositions;
  }
}

export const getTableStructureFromElement = memoizeOne(
  (editor: Editor, tableElement: TableElement) =>
    new TableStructure(editor, tableElement)
) as (editor: Editor, tableElement: TableElement) => TableStructure;

export const getTableStructureFromSelection = (editor: Editor) => {
  const table = TableUtils.currentTable(editor);
  if (!table) return;

  return getTableStructureFromElement(editor, table[0]);
};

export default TableStructure;
